import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "@material-ui/core/styles";
import { isPast, isToday, parseISO } from "date-fns";
import moment from "moment";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "moment/locale/pt-br";
import "moment/locale/es";
import "moment/locale/en-gb";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { i18n } from "../../translate/i18n";
import TaskDayDetailModal from "../TaskDayDetailModal";
import "./TasksCalendar.css";

const localizer = momentLocalizer(moment);

const STATUS_COLORS = {
  completed: { bg: "#059669", border: "#047857" },
  overdue: { bg: "#DC2626", border: "#B91C1C" },
  today: { bg: "#D97706", border: "#B45309" },
  upcoming: { bg: "#2563EB", border: "#1D4ED8" },
};

const getTaskVisualStatus = (task) => {
  if (task.status === "completed") return "completed";
  const date = parseISO(task.remindAt);
  if (isPast(date) && !isToday(date)) return "overdue";
  if (isToday(date)) return "today";
  return "upcoming";
};

const getCalendarMessages = () => {
  const currentLanguage = i18n.language;

  if (currentLanguage === "es") {
    return {
      date: "Fecha",
      time: "Hora",
      event: "Tarea",
      allDay: "Todo el día",
      week: "Semana",
      work_week: "Semana laboral",
      day: "Día",
      month: "Mes",
      previous: "Anterior",
      next: "Siguiente",
      yesterday: "Ayer",
      tomorrow: "Mañana",
      today: "Hoy",
      agenda: "Agenda",
      noEventsInRange: "No hay tareas en este período.",
      showMore: (total) => `+${total} ${i18n.t("tasks.calendar.more")}`,
    };
  }

  if (currentLanguage === "en") {
    return {
      date: "Date",
      time: "Time",
      event: "Task",
      allDay: "All day",
      week: "Week",
      work_week: "Work week",
      day: "Day",
      month: "Month",
      previous: "Previous",
      next: "Next",
      yesterday: "Yesterday",
      tomorrow: "Tomorrow",
      today: "Today",
      agenda: "Agenda",
      noEventsInRange: "No tasks in this range.",
      showMore: (total) => `+${total} more`,
    };
  }

  return {
    date: "Data",
    time: "Hora",
    event: "Tarefa",
    allDay: "Dia todo",
    week: "Semana",
    work_week: "Semana",
    day: "Dia",
    month: "Mês",
    previous: "Anterior",
    next: "Próximo",
    yesterday: "Ontem",
    tomorrow: "Amanhã",
    today: "Hoje",
    agenda: "Agenda",
    noEventsInRange: "Não há tarefas neste período.",
    showMore: (total) => `+${total} mais`,
  };
};

const TaskEvent = ({ event, view }) => {
  const task = event.resource;
  const timeLabel = moment(event.start).format("HH:mm");
  const isMonthView = view === "month";

  return (
    <div
      className={`task-event ${isMonthView ? "task-event--month" : ""}`}
      title={[timeLabel, task?.title, task?.body].filter(Boolean).join(" · ")}
    >
      <span className="task-event__time">{timeLabel}</span>
      <span className="task-event__title">{event.title}</span>
    </div>
  );
};

const buildCalendarComponents = (view) => ({
  event: (props) => <TaskEvent {...props} view={view} />,
});

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Evita que tareas cerca de medianoche se dibujen cruzando al día siguiente. */
const buildEventRange = (remindAt) => {
  const start = moment(remindAt);
  let end = start.clone().add(15, "minutes");
  if (!end.isSame(start, "day")) {
    end = start.clone().add(1, "minute");
  }
  return { start: start.toDate(), end: end.toDate() };
};

const TasksCalendar = ({
  tasks,
  onEdit,
  onComplete,
  onDelete,
  onGoToTicket,
  onCreateForDate,
}) => {
  const theme = useTheme();
  const [calendarView, setCalendarView] = useState("month");
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [highlightTaskId, setHighlightTaskId] = useState(null);

  useEffect(() => {
    const currentLanguage = i18n.language;
    if (currentLanguage === "es") {
      moment.locale("es");
    } else if (currentLanguage === "en") {
      moment.locale("en-gb");
    } else {
      moment.locale("pt-br");
    }
  }, [i18n.language]);

  const calendarEvents = useMemo(
    () =>
      tasks.map((task) => {
        const { start, end } = buildEventRange(task.remindAt);
        return {
          id: task.id,
          title: task.title,
          start,
          end,
          resource: task,
        };
      }),
    [tasks]
  );

  const tasksForSelectedDay = useMemo(() => {
    if (!selectedDate) return [];
    return tasks.filter((task) => isSameDay(parseISO(task.remindAt), selectedDate));
  }, [tasks, selectedDate]);

  const openDayModal = (date, taskId = null) => {
    setSelectedDate(date);
    setHighlightTaskId(taskId);
    setDayModalOpen(true);
  };

  const handleSelectEvent = (event) => {
    if (event?.start) {
      openDayModal(event.start, event?.resource?.id || null);
    }
  };

  const handleSelectSlot = ({ start }) => {
    openDayModal(start, null);
  };

  const getEventPropGetter = (event) => {
    const visualStatus = getTaskVisualStatus(event?.resource);
    const colors = STATUS_COLORS[visualStatus] || STATUS_COLORS.upcoming;

    if (calendarView === "agenda") {
      return {
        className: `task-agenda-row task-agenda-row--${visualStatus}`,
        style: {
          backgroundColor: "transparent",
          borderColor: "transparent",
          color: "inherit",
        },
      };
    }

    return {
      className: `task-event-row task-event-row--${visualStatus}`,
      style: {
        backgroundColor: colors.bg,
        borderColor: colors.border,
        color: "#ffffff",
      },
    };
  };

  return (
    <>
      <div
        className={`tasks-calendar tasks-calendar--${theme.mode === "light" ? "light" : "dark"}`}
      >
        <Calendar
          messages={getCalendarMessages()}
          formats={{
            agendaDateFormat: "DD/MM ddd",
            agendaTimeFormat: "HH:mm",
            agendaTimeRangeFormat: ({ start, end }, culture, localizerInstance) =>
              `${localizerInstance.format(start, "HH:mm", culture)} – ${localizerInstance.format(end, "HH:mm", culture)}`,
            dayFormat: "DD ddd",
            weekdayFormat: "dddd",
            monthHeaderFormat: "MMMM YYYY",
            dayHeaderFormat: "dddd DD/MM",
            dayRangeHeaderFormat: ({ start, end }, culture, localizerInstance) =>
              `${localizerInstance.format(start, "DD MMM", culture)} – ${localizerInstance.format(end, "DD MMM", culture)}`,
            timeGutterFormat: "HH:mm",
            eventTimeRangeFormat: ({ start, end }, culture, localizerInstance) =>
              `${localizerInstance.format(start, "HH:mm", culture)} – ${localizerInstance.format(end, "HH:mm", culture)}`,
          }}
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          views={["month", "week", "day", "agenda"]}
          view={calendarView}
          onView={setCalendarView}
          popup
          selectable
          dayLayoutAlgorithm="no-overlap"
          scrollToTime={new Date(1970, 0, 1, 8, 0, 0)}
          components={buildCalendarComponents(calendarView)}
          eventPropGetter={getEventPropGetter}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          style={{ height: "100%", minHeight: 560 }}
        />
      </div>

      <TaskDayDetailModal
        open={dayModalOpen}
        onClose={() => {
          setDayModalOpen(false);
          setHighlightTaskId(null);
        }}
        date={selectedDate}
        tasks={tasksForSelectedDay}
        highlightTaskId={highlightTaskId}
        onEdit={(task) => {
          setDayModalOpen(false);
          onEdit(task);
        }}
        onComplete={onComplete}
        onDelete={onDelete}
        onGoToTicket={onGoToTicket}
        onCreate={
          onCreateForDate
            ? (date) => {
                setDayModalOpen(false);
                onCreateForDate(date);
              }
            : undefined
        }
      />
    </>
  );
};

export default TasksCalendar;
